import React from 'react';
import Tooltip from '@/components/Tooltip';
import OpenAILogo from '@/components/icons/OpenAILogo';

// KeyStatTooltipContent helper component for tooltips
const KeyStatTooltipContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-64 p-2 text-xs font-mono">
    {children}
  </div>
);

// StatCard component to standardize the stat block appearance
const StatCard: React.FC<{
  title: string;
  value: string;
  footer: string;
  tooltipContent: React.ReactNode;
  isOpenBrain?: boolean;
}> = ({ title, value, footer, tooltipContent, isOpenBrain = false }) => (
  <Tooltip content={<KeyStatTooltipContent>{tooltipContent}</KeyStatTooltipContent>}>
    <div className={`px-4 py-2 rounded-md h-full ${isOpenBrain ? "bg-[var(--accent)] text-white" : "border border-black"} relative`}>
      {isOpenBrain && (
        <div className="absolute top-2 right-2">
          <OpenAILogo size={20} color="white" />
        </div>
      )}
      <div className={`text-[10px] uppercase mb-1 ${isOpenBrain ? "text-white" : "text-gray-500"}`}>{title}</div>
      <div className="text-2xl font-bold">{value}</div>
      <div className={`text-[10px] ${isOpenBrain ? "text-white" : "text-gray-500"}`}>{footer}</div>
    </div>
  </Tooltip>
);

const KeyMetrics2026: React.FC = () => {
  return (
    <div className="font-mono mt-8 mb-8">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Global AI Title + First Stat */}
        <div className="order-10 lg:order-none">
          <StatCard 
            title="GLOBAL AI CAPEX"
            value="$1T"
            footer="COST OF OWNERSHIP OF ACTIVE COMPUTE"
            tooltipContent={"Total global capital expenditure on AI infrastructure."}
          />
        </div>

        {/* Title - spans 2 columns */}
        <div className="col-span-2">
          <h2 className="text-[40px] font text-right not-italic mt-0 mb-0 font-bold font-sans tracking-tight uppercase">KEY METRICS 2026</h2>
          {/* <h3 className="text-xl font-bold mb-4 uppercase italic bg-[var(--accent)] text-white inline-block px-2 py-1">OPENMIND</h3> */}
        </div>

        {/* Global AI stats - row 2 */}
        <div className="order-11 lg:order-none">
          <StatCard 
            title="GLOBAL AI POWER"
            value="38GW"
            footer="PEAK POWER"
            tooltipContent={"Global peak power consumption by AI systems."}
          />
        </div>
        
        {/* OpenBrain stats - row 1, columns 1-2 */}
        <div className="order-1 lg:order-none">
          <StatCard 
            title="OPENBRAIN REVENUE"
            value="$35B"
            footer="2026 ANNUAL"
            tooltipContent={"OpenBrain's projected annual revenue for 2026."}
            isOpenBrain={true}
          />
        </div>
        
        <div className="order-2 lg:order-none">
          <StatCard 
            title="CAPITAL EXPENDITURE"
            value="$200B"
            footer="COST OF OWNERSHIP OF OPENBRAIN'S ACTIVE COMPUTE"
            tooltipContent={"Total capital expenditure for OpenBrain's computing infrastructure."}
            isOpenBrain={true}
          />
        </div>

        {/* Global AI stats - row 3 */}
        <div className="order-12 lg:order-none">
          <StatCard 
            title="SHARE OF US POWER ON AI"
            value="2.5%"
            footer="33 GW OF 1.34TW CAPACITY"
            tooltipContent={"Percentage of US electrical capacity used for AI operations."}
          />
        </div>
        
        
        
        {/* OpenBrain stats - row 2, columns 1-2 */}
        <div className="order-3 lg:order-none">
          <StatCard 
            title="OPENBRAIN COMPUTE COSTS"
            value="$40B"
            footer="2026 ANNUAL"
            tooltipContent={"Annual compute costs for OpenBrain operations."}
            isOpenBrain={true}
          />
        </div>
        
        <div className="order-4 lg:order-none">
          <StatCard 
            title="OPENBRAIN POWER REQUIREMENT"
            value="6GW"
            footer="PEAK POWER"
            tooltipContent={"Peak power requirements for OpenBrain's computing infrastructure."}
            isOpenBrain={true}
          />
        </div>
        
        
        
        
      </div>
    </div>
  );
};

export default KeyMetrics2026;
